'use client'

import { useTranslations } from 'next-intl'
import { Link2, MessageCircle, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Users, Clock } from 'lucide-react'
import MockupChrome from './MockupChrome'
import '../../sass/landing/BookingDashboardMockup.scss'

interface Reservation {
  id: 'r1' | 'r2' | 'r3' | 'r4' | 'r5' | 'r6' | 'r7' | 'r8' | 'r9' | 'r10' | 'r11'
  tableId: 'S1' | 'S2' | 'S3' | 'T1' | 'T2' | 'T3' | 'T4' | 'T5'
  startCol: number
  span: number
  customer: 'lauraVazquez' | 'rafaGomez' | 'pabloRodriguez' | 'laraPeleteiro' | 'pedroValdes' | 'manuelAlvarez' | 'cristinaVilmex' | 'albertoSanchez' | 'miriamContreras' | 'pabloVillaroel'
  party: number
  joined?: boolean
  note?: boolean
}

interface Table {
  id: 'S1' | 'S2' | 'S3' | 'T1' | 'T2' | 'T3' | 'T4' | 'T5'
  capacity: string
  joinable?: boolean
}

const hours = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00']

const tables: Table[] = [
  { id: 'S1', capacity: '1 - 2', joinable: true },
  { id: 'S2', capacity: '2 - 2', joinable: true },
  { id: 'S3', capacity: '4 - 4' },
  { id: 'T1', capacity: '2 - 4' },
  { id: 'T2', capacity: '2 - 4' },
  { id: 'T3', capacity: '2 - 4' },
  { id: 'T4', capacity: '2 - 4' },
  { id: 'T5', capacity: '2 - 4' }
]

const reservations: Reservation[] = [
  { id: 'r1', tableId: 'S1', startCol: 1, span: 2, customer: 'lauraVazquez', party: 2, note: true },
  { id: 'r2', tableId: 'S1', startCol: 3, span: 2, customer: 'rafaGomez', party: 4, joined: true },
  { id: 'r3', tableId: 'S2', startCol: 3, span: 2, customer: 'rafaGomez', party: 4, joined: true },
  { id: 'r4', tableId: 'S3', startCol: 1, span: 2, customer: 'pabloRodriguez', party: 4 },
  { id: 'r5', tableId: 'S3', startCol: 4, span: 2, customer: 'laraPeleteiro', party: 4 },
  { id: 'r6', tableId: 'T1', startCol: 1, span: 2, customer: 'pedroValdes', party: 4 },
  { id: 'r7', tableId: 'T2', startCol: 1, span: 2, customer: 'manuelAlvarez', party: 4 },
  { id: 'r8', tableId: 'T2', startCol: 3, span: 2, customer: 'cristinaVilmex', party: 4 },
  { id: 'r9', tableId: 'T4', startCol: 1, span: 2, customer: 'albertoSanchez', party: 4 },
  { id: 'r10', tableId: 'T5', startCol: 1, span: 2, customer: 'miriamContreras', party: 2, note: true },
  { id: 'r11', tableId: 'T5', startCol: 4, span: 2, customer: 'pabloVillaroel', party: 2 }
]

const BookingDashboardMockup = () => {
  const t = useTranslations('LandingV2.mockups.bookings')

  return (
    <div className='booking-dashboard-mockup' aria-label={t('ariaLabel')}>
      <MockupChrome
        icon={<CalendarIcon size={11} strokeWidth={2.4} />}
        address={t('address')}
      />

      <div className='booking-dashboard-mockup__app'>
        <div className='booking-dashboard-mockup__toolbar'>
          <div className='booking-dashboard-mockup__nav'>
            <button className='booking-dashboard-mockup__icon-btn' aria-label={t('prevDayAria')} type='button' tabIndex={-1}>
              <ChevronLeft size={14} />
            </button>
            <button className='booking-dashboard-mockup__icon-btn' aria-label={t('nextDayAria')} type='button' tabIndex={-1}>
              <ChevronRight size={14} />
            </button>
            <div className='booking-dashboard-mockup__date'>
              <CalendarIcon size={12} />
              <span>{t('today')}</span>
              <span className='booking-dashboard-mockup__date-meta'>{t('dateMeta')}</span>
            </div>
          </div>
          <div className='booking-dashboard-mockup__stats'>
            <div className='booking-dashboard-mockup__stat'>
              <Users size={11} />
              <span>{t('reservationsCount')}</span>
            </div>
            <div className='booking-dashboard-mockup__stat'>
              <Clock size={11} />
              <span>{t('tablesCount')}</span>
            </div>
          </div>
        </div>

        <div className='booking-dashboard-mockup__grid'>
          <div className='booking-dashboard-mockup__corner'>
            <span>{t('tablesLabel')}</span>
          </div>
          <div className='booking-dashboard-mockup__hours'>
            {hours.map((h) => (
              <div key={h} className='booking-dashboard-mockup__hour'>{h}</div>
            ))}
          </div>

          <div className='booking-dashboard-mockup__rows'>
            {tables.map((table) => (
              <div key={table.id} className='booking-dashboard-mockup__row'>
                <div className='booking-dashboard-mockup__row-label'>
                  <span className='booking-dashboard-mockup__table-id'>{table.id}</span>
                  <span className='booking-dashboard-mockup__table-cap'>{table.capacity}</span>
                  {table.joinable && (
                    <span className='booking-dashboard-mockup__joinable' title={t('joinableTitle')}>
                      <Link2 size={10} strokeWidth={2.5} />
                    </span>
                  )}
                </div>
                <div className='booking-dashboard-mockup__row-track'>
                  {reservations
                    .filter((r) => r.tableId === table.id)
                    .map((r) => (
                      <article
                        key={r.id}
                        className='booking-dashboard-mockup__reservation'
                        style={{
                          gridColumn: `${r.startCol} / span ${r.span}`
                        }}
                      >
                        <div className='booking-dashboard-mockup__reservation-bar' aria-hidden='true' />
                        <div className='booking-dashboard-mockup__reservation-body'>
                          <span className='booking-dashboard-mockup__reservation-name'>{t(`customers.${r.customer}`)}</span>
                          <span className='booking-dashboard-mockup__reservation-time'>{t(`reservationTimes.${r.id}`)}</span>
                        </div>
                        <div className='booking-dashboard-mockup__reservation-foot'>
                          <span className='booking-dashboard-mockup__reservation-party'>{r.party}</span>
                          {r.joined && (
                            <span className='booking-dashboard-mockup__reservation-badge'>
                              <Link2 size={9} strokeWidth={2.5} />
                            </span>
                          )}
                          {r.note && (
                            <span className='booking-dashboard-mockup__reservation-badge booking-dashboard-mockup__reservation-badge--msg'>
                              <MessageCircle size={9} strokeWidth={2.5} />
                            </span>
                          )}
                        </div>
                      </article>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingDashboardMockup
